import { useState } from 'react'
import { Grid, Typography, Box, Tabs, Tab } from '@mui/material'

import { ClientFundTile } from './client-fund-tile'
import { PendingInviteTile } from './pending-invite-tile'
import { DeclinedInviteTile } from './declined-invite-tile'
import { SecuritiesInviteTile } from './securities-invite-tile'

export function ClientFundGrid({
  funds,
  pendingInvites,
  declinedInvites,
  securitiesInvites,
  onResendSuccess,
  onCancelSuccess,
  onAcceptSuccess,
  onReinviteSuccess,
}) {
  const [activeTab, setActiveTab] = useState(0)

  const hasFunds = funds && funds.length > 0
  const hasPendingInvites = pendingInvites && pendingInvites.length > 0
  const hasDeclinedInvites = declinedInvites && declinedInvites.length > 0
  const hasSecuritiesInvites = securitiesInvites && securitiesInvites.length > 0

  // Filter funds by status
  const activeFunds =
    funds?.filter((fund) => {
      const invite = fund.fundClaimInvite
      return !invite || invite.statusLabel === 'claimed'
    }) || []

  const pendingFunds =
    funds?.filter((fund) => {
      const invite = fund.fundClaimInvite
      return invite?.statusLabel === 'pending' || invite?.statusLabel === 'expired'
    }) || []

  // Get items to display based on active tab
  const getDisplayItems = () => {
    switch (activeTab) {
      case 1: // Active
        return { funds: activeFunds, invites: [], secInvites: [], declined: [] }
      case 2: // Pending
        return { funds: pendingFunds, invites: pendingInvites || [], secInvites: securitiesInvites || [], declined: [] }
      case 3: // Declined
        return { funds: [], invites: [], secInvites: [], declined: declinedInvites || [] }
      default: // All
        return { funds: funds || [], invites: pendingInvites || [], secInvites: securitiesInvites || [], declined: [] }
    }
  }

  const {
    funds: displayFunds,
    invites: displayInvites,
    secInvites: displaySecInvites,
    declined: displayDeclined,
  } = getDisplayItems()
  const hasDisplayItems =
    displayFunds.length > 0 || displayInvites.length > 0 || displaySecInvites.length > 0 || displayDeclined.length > 0

  if (!hasFunds && !hasPendingInvites && !hasSecuritiesInvites && !hasDeclinedInvites) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No client funds to display
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          You don&apos;t have any client funds assigned to you yet.
        </Typography>
      </Box>
    )
  }

  const allCount = (funds?.length || 0) + (pendingInvites?.length || 0) + (securitiesInvites?.length || 0)
  const activeCount = activeFunds.length
  const pendingCount = pendingFunds.length + (pendingInvites?.length || 0) + (securitiesInvites?.length || 0)
  const declinedCount = declinedInvites?.length || 0

  return (
    <Box>
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label={`All (${allCount})`} />
        <Tab label={`Active (${activeCount})`} />
        <Tab label={`Pending (${pendingCount})`} />
        {hasDeclinedInvites && <Tab label={`Declined (${declinedCount})`} />}
      </Tabs>

      {!hasDisplayItems ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No {{ 1: 'active', 3: 'declined' }[activeTab] || 'pending'} funds to display
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Securities advisor connection requests */}
          {displaySecInvites.map((invite) => (
            <Grid item xs="auto" key={`sec-invite-${invite.id}`}>
              <SecuritiesInviteTile
                invite={invite}
                onAcceptSuccess={onAcceptSuccess}
                onReinviteSuccess={onReinviteSuccess}
              />
            </Grid>
          ))}
          {/* Pending invites (awaiting acceptance) */}
          {displayInvites.map((invite) => (
            <Grid item xs="auto" key={`invite-${invite.id}`}>
              <PendingInviteTile invite={invite} onResendSuccess={onResendSuccess} onCancelSuccess={onCancelSuccess} />
            </Grid>
          ))}
          {/* Invites the donor declined */}
          {displayDeclined.map((invite) => (
            <Grid item xs="auto" key={`declined-invite-${invite.id}`}>
              <DeclinedInviteTile invite={invite} />
            </Grid>
          ))}
          {/* Funds the advisor has access to */}
          {displayFunds.map((fund) => (
            <Grid item xs="auto" key={fund.id}>
              <ClientFundTile fund={fund} onResendSuccess={onResendSuccess} onCancelSuccess={onCancelSuccess} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}
